import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Checkbox } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import * as S from 'commons/components/Table/Table.styles';
import { IDeliveryNote } from 'commons/types';
import {
  DefaultSortProps,
  RowTitleProps,
  SortOrder,
} from 'commons/types/listPageData.interface';
import { formatDate } from 'commons/utils/formatDate';
import { DataTable, TableActionRow } from 'pages/dashboard/commons/components';
import { getDeliveryTypeShort } from 'pages/delivery-note/utils';
import { cancelDeliveryNoteBulkAction } from 'redux-stores/actions/dashboard.action';
import { getDeliveryNoteDetailAction } from 'redux-stores/actions/deliveryNote.action';
import { RootReducerInterface } from 'redux-stores/reducers';
import { setSelectedDeliveryNote } from 'redux-stores/reducers/deliveryNoteReducer';
import { AppDispatch } from 'redux-stores/store';

import BulkActionBtnMenu from './BulkActionBtnMenu.component';
import { DeliveryNoteDetailDialog } from './DeliveryNoteDetailDialog.component';
import DeliveryNoteRowActionBtn from './DeliveryNoteRowActionBtn.component';

const rowTitles: RowTitleProps[] = [
  { title: 'AWB Number' },
  { title: 'Status', isCentered: true },
  { title: 'Delivery Type', isCentered: true },
  { title: 'Cust ID', isCentered: true },
  { title: 'Priority', sortField: 'priority', isCentered: true },
  { title: 'Order Time', sortField: 'ordered_at', isCentered: true },
  { title: 'Cutoff Time', sortField: 'cutoff_time', isCentered: true },
  { title: 'Dispatch Time', sortField: 'dispatch_time', isCentered: true },
  { title: 'Latest Update', sortField: 'updatedAt', isCentered: true },
  { title: 'Total Order', isCentered: true },
];

const defaultSort: DefaultSortProps = {
  sortField: 'ordered_at',
  sortOrder: SortOrder.DESC,
};

const Row: React.FC<{
  data: IDeliveryNote;
  index: number;
  checked: boolean;
  onCheckboxClick: (awbNumber: string) => void;
  onDetailsClick: (awbNumber: string) => void;
}> = ({ data, index, checked, onCheckboxClick, onDetailsClick }) => (
  <S.TableRow>
    <S.TableColumn>
      <Checkbox
        data-testid={`checkboxDeliveryNote{${index + 1}}`}
        onClick={() => onCheckboxClick(data.awb_number)}
        checked={checked}
      />
    </S.TableColumn>
    <S.TableColumn data-testid={`textDeliveryNoteAWB{${index + 1}}`}>
      {data.awb_number}
    </S.TableColumn>
    <S.TableColumn
      style={{ textTransform: 'capitalize', textAlign: 'center' }}
      data-testid={`textDeliveryNoteStatus{${index + 1}}`}
    >
      {data.status.replace(/_/g, ' ')}
    </S.TableColumn>
    <S.TableColumn
      data-testid={`textDeliveryNoteType{${index + 1}}`}
      style={{ textAlign: 'center' }}
    >
      {getDeliveryTypeShort(
        data.is_regular_delivery,
        data.is_coldchain_delivery,
      )}
    </S.TableColumn>
    <S.TableColumn
      data-testid={`textDeliveryNotePharmacyId{${index + 1}}`}
      style={{ textAlign: 'center' }}
    >
      {data.pharmacy_id}
    </S.TableColumn>
    <S.TableColumn
      data-testid={`textDeliveryNotePriority{${index + 1}}`}
      style={{ textAlign: 'center' }}
    >
      {data.priority ?? '-'}
    </S.TableColumn>
    <S.TableColumn
      style={{ textAlign: 'center' }}
      data-testid={`textDeliveryNoteOrderedAt{${index + 1}}`}
    >
      {formatDate(data.ordered_at, 'dd/MM/yyyy HH:mm')}
    </S.TableColumn>
    <S.TableColumn
      style={{ textAlign: 'center' }}
      data-testid={`textDeliveryNoteCutoffTime{${index + 1}}`}
    >
      {data.cutoff_time
        ? formatDate(data.cutoff_time, 'dd/MM/yyyy HH:mm')
        : '-'}
    </S.TableColumn>
    <S.TableColumn
      style={{ textAlign: 'center' }}
      data-testid={`textDeliveryNoteDispatchTime{${index + 1}}`}
    >
      {data.expected_dispatch_time
        ? formatDate(data.expected_dispatch_time, 'dd/MM/yyyy HH:mm')
        : '-'}
    </S.TableColumn>
    <S.TableColumn
      style={{ textAlign: 'center' }}
      data-testid={`textDeliveryNoteUpdatedAt{${index + 1}}`}
    >
      {data.updatedAt ? formatDate(data.updatedAt, 'dd/MM/yyyy HH:mm') : '-'}
    </S.TableColumn>
    <S.TableColumn
      style={{ textAlign: 'center' }}
      data-testid={`textDeliveryNoteTotalOrder{${index + 1}}`}
    >
      {data.picking_task_count}
    </S.TableColumn>
    <S.TableColumn style={{ textAlign: 'right' }}>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button
          data-testid={`buttonDeliveryNoteDetails{${index + 1}}`}
          onClick={() => onDetailsClick(data.awb_number)}
          variant="outlined"
          sx={{
            width: 87,
          }}
        >
          Details
        </Button>
        <DeliveryNoteRowActionBtn deliveryNote={data} />
      </div>
    </S.TableColumn>
  </S.TableRow>
);

const DeliveryNoteTable: React.FC = (): React.ReactNode => {
  const dispatch = useDispatch<AppDispatch>();
  const { deliveryNoteList } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );
  const { selectedDeliveryNote } = useSelector(
    (state: RootReducerInterface) => state.deliveryNote,
  );

  const [isDetailOpen, setIsDetailOpen] = useState<boolean>(false);
  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [checkedAwbs, setCheckedAwbs] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openCancelConfirm, setOpenCancelConfirm] = useState<boolean>(false);
  const openMenu = Boolean(anchorEl);

  const selectedDeliveryNotes = deliveryNoteList.filter((item) =>
    checkedAwbs.includes(item.awb_number),
  );
  const selectedAwbNumbers = selectedDeliveryNotes.map(
    (item) => item.awb_number,
  );

  const handleCheckItem = (awbNumber: string): void => {
    if (checkedAwbs.includes(awbNumber)) {
      setCheckedAwbs(checkedAwbs.filter((awb) => awb !== awbNumber));
    } else {
      setCheckedAwbs([...checkedAwbs, awbNumber]);
    }
  };

  const handleCheckAll = (): void => {
    if (checkAll) {
      setCheckedAwbs([]);
    } else {
      setCheckedAwbs(deliveryNoteList.map((item) => item.awb_number));
    }
    setCheckAll(!checkAll);
  };

  const handleClickMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (): void => {
    setAnchorEl(null);
  };

  const handleDetailsClick = (awbNumber: string): void => {
    dispatch(getDeliveryNoteDetailAction(awbNumber));
    setIsDetailOpen(true);
  };

  const handleCloseDetail = (): void => {
    dispatch(setSelectedDeliveryNote(null));
    setIsDetailOpen(false);
  };

  const handleConfirmBulkCancel = (): void => {
    dispatch(cancelDeliveryNoteBulkAction(selectedAwbNumbers));
  };

  return (
    <>
      <TableActionRow
        dataName="delivery note"
        selectedData={selectedDeliveryNotes}
        anchorEl={anchorEl}
        openMenu={openMenu}
        handleClickMenu={handleClickMenu}
      >
        <BulkActionBtnMenu
          selectedDeliveryNotes={selectedDeliveryNotes}
          handleCloseMenu={handleCloseMenu}
          handleOpenCancelConfirm={() => setOpenCancelConfirm(true)}
        />
      </TableActionRow>
      <DataTable
        handleCheckAll={handleCheckAll}
        checkAll={checkAll}
        rowTitles={rowTitles}
        defaultSort={defaultSort}
      >
        <S.TableBody>
          {deliveryNoteList.map((item, index) => (
            <Row
              data={item}
              key={item.awb_number}
              index={index}
              checked={checkedAwbs.includes(item.awb_number)}
              onCheckboxClick={handleCheckItem}
              onDetailsClick={handleDetailsClick}
            />
          ))}
        </S.TableBody>
      </DataTable>
      {deliveryNoteList.length > 0 && (
        <TableActionRow
          dataName="delivery note"
          selectedData={selectedDeliveryNotes}
          anchorEl={anchorEl}
          openMenu={openMenu}
          handleClickMenu={handleClickMenu}
        >
          <BulkActionBtnMenu
            selectedDeliveryNotes={selectedDeliveryNotes}
            handleCloseMenu={handleCloseMenu}
            handleOpenCancelConfirm={() => setOpenCancelConfirm(true)}
          />
        </TableActionRow>
      )}
      <DeliveryNoteDetailDialog
        open={isDetailOpen}
        deliveryNote={selectedDeliveryNote}
        onClose={handleCloseDetail}
      />
      <ConfirmationDialog
        title="Cancel Delivery Note"
        content={`Are you sure you want to cancel <b>${selectedAwbNumbers.length}</b> delivery note(s)?`}
        openModal={openCancelConfirm}
        onClose={() => setOpenCancelConfirm(false)}
        onConfirm={handleConfirmBulkCancel}
      />
    </>
  );
};

export default DeliveryNoteTable;
